import { Copy, ExternalLink, Link2Off, Share } from "lucide-react";
import { useEffect, useState } from "react";
import { useToast } from "../hooks/useToast";
import { Button } from "./ui/button";

interface ShareModalProps {
    isOpen: boolean;
    onClose: () => void;
    shareUrl: string;
    storyTitle: string;
    onRevoke: () => void;
    isRevoking?: boolean;
}

function isWebShareSupported(): boolean {
    return typeof navigator !== "undefined" && "share" in navigator;
}

export function ShareModal({ isOpen, onClose, shareUrl, storyTitle, onRevoke, isRevoking = false }: ShareModalProps) {
    const [copySuccess, setCopySuccess] = useState(false);
    const { toast } = useToast();

    // Handle escape key and body scroll - following existing modal pattern
    useEffect(() => {
        const handleEscape = (event: KeyboardEvent) => {
            if (event.key === "Escape" && !isRevoking) {
                onClose();
            }
        };

        if (isOpen) {
            document.addEventListener("keydown", handleEscape);
            document.body.style.overflow = "hidden";
        }

        return () => {
            document.removeEventListener("keydown", handleEscape);
            document.body.style.overflow = "unset";
        };
    }, [isOpen, onClose, isRevoking]);

    // Handle backdrop click
    const handleBackdropClick = (event: React.MouseEvent<HTMLDivElement>) => {
        if (event.target === event.currentTarget && !isRevoking) {
            onClose();
        }
    };

    const handleCopyLink = async () => {
        try {
            await navigator.clipboard.writeText(shareUrl);
            setCopySuccess(true);
            toast({
                title: "Link copied!",
                description: "Share link copied to clipboard.",
                variant: "success",
            });
            setTimeout(() => setCopySuccess(false), 2000);
        } catch (error) {
            console.error("Failed to copy link:", error);
            toast({
                title: "Failed to copy link",
                description: "Please copy the link manually.",
                variant: "destructive",
            });
        }
    };

    const handleNativeShare = async () => {
        if (!isWebShareSupported()) return;

        try {
            await navigator.share({
                title: `${storyTitle} - StoryDoodle`,
                text: "I created this story on StoryDoodle. Take a look!",
                url: shareUrl,
            });
        } catch (error) {
            // User cancelled sharing or error occurred
            if ((error as Error).name !== "AbortError") {
                console.error("Failed to share:", error);
                toast({
                    title: "Sharing failed",
                    description: "Please try copying the link instead.",
                    variant: "destructive",
                });
            }
        }
    };

    if (!isOpen) return null;

    return (
        <div
            className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4 overflow-y-auto"
            onClick={handleBackdropClick}
        >
            <div className="max-w-md w-full p-6 space-y-6 shadow-2xl animate-in fade-in-0 zoom-in-95 duration-200 page-gradient md:bg-background border border-border md:border-primary/20 rounded-lg my-4 sm:my-8">
                {/* Header */}
                <div className="space-y-2">
                    <h3 className="text-lg font-semibold flex items-center gap-2">
                        <Share className="h-5 w-5 text-primary" />
                        Share Your Story
                    </h3>
                    <p className="text-sm text-muted-foreground">
                        Anyone with this link can view your completed story.
                    </p>
                </div>

                {/* Share URL Display */}
                <div className="space-y-2">
                    <label className="text-sm font-medium">Share Link</label>
                    <div className="flex gap-2">
                        <div className="flex-1 p-3 bg-muted rounded-lg border text-sm font-mono break-all">
                            {shareUrl}
                        </div>
                    </div>
                </div>

                {/* Share Actions */}
                <div className="flex gap-2">
                    <Button
                        onClick={handleCopyLink}
                        variant={isWebShareSupported() ? "outline" : "default"}
                        className="flex-1 gap-2"
                        disabled={copySuccess}
                    >
                        <Copy className="h-4 w-4" />
                        Copy Link
                    </Button>
                    {isWebShareSupported() && (
                        <Button onClick={handleNativeShare} variant="default" className="flex-1 gap-2">
                            <Share className="h-4 w-4" />
                            Share
                        </Button>
                    )}
                </div>

                {/* Preview Link */}
                <div>
                    <Button
                        variant="ghost"
                        size="sm"
                        className="w-full gap-2 text-muted-foreground hover:text-foreground"
                        onClick={() => window.open(shareUrl, "_blank")}
                    >
                        <ExternalLink className="h-4 w-4" />
                        Preview shared story
                    </Button>
                </div>

                {/* Revoke Section */}
                <div className="pt-2 border-t space-y-3">
                    <h4 className="text-sm font-medium text-muted-foreground">Manage Sharing</h4>
                    <div className="flex gap-2">
                        <Button
                            onClick={onRevoke}
                            variant="ghost"
                            size="sm"
                            disabled={isRevoking}
                            className="flex-1 gap-2 text-destructive hover:text-destructive/80 hover:bg-destructive/10"
                        >
                            <Link2Off className="h-4 w-4" />
                            {isRevoking ? "Stopping..." : "Stop Sharing"}
                        </Button>
                        <Button onClick={onClose} variant="outline" size="sm" className="flex-1">
                            Close
                        </Button>
                    </div>
                </div>
            </div>
        </div>
    );
}
